import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { 
  motion, 
  useScroll, 
  useTransform, 
  useSpring,
  useMotionValue,
  AnimatePresence 
} from 'framer-motion';
import { 
  FaCode, 
  FaMobile, 
  FaDesktop, 
  FaCloud, 
  FaDatabase, 
  FaChartLine, 
  FaUserCog,
  FaShieldAlt,
  FaChevronRight,
  FaArrowRight,
  FaChevronDown,
  FaRocket,
  FaBrain,
  FaEye,
  FaCogs,
  FaVrCardboard
} from 'react-icons/fa';
import useIsMobile from '../hooks/useIsMobile';

// Modern Hero Section with Floating Elements
const HeroSection = styled.section`
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background: 
    linear-gradient(135deg, #ffffff 0%, #f8f9ff 25%, #ffffff 50%, #f5f7ff 75%, #ffffff 100%),
    radial-gradient(circle at 30% 70%, rgba(0, 122, 255, 0.05) 0%, transparent 50%),
    radial-gradient(circle at 70% 30%, rgba(255, 59, 48, 0.04) 0%, transparent 50%),
    radial-gradient(circle at 20% 20%, rgba(48, 209, 88, 0.03) 0%, transparent 50%);
  perspective: 1000px;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      linear-gradient(90deg, rgba(0, 122, 255, 0.02) 1px, transparent 1px),
      linear-gradient(rgba(0, 122, 255, 0.02) 1px, transparent 1px);
    background-size: 50px 50px;
    opacity: 0.4;
    animation: meshMove 25s linear infinite, meshWave 10s ease-in-out infinite;
  }
  
  @keyframes meshMove {
    0% { transform: translate(0, 0) rotate(0deg) skewX(0deg); }
    25% { transform: translate(25px, 12px) rotate(1deg) skewX(1deg); }
    50% { transform: translate(50px, 50px) rotate(0deg) skewX(-1deg); }
    75% { transform: translate(25px, 37px) rotate(-1deg) skewX(0.5deg); }
    100% { transform: translate(0, 0) rotate(0deg) skewX(0deg); }
  }
  
  @keyframes meshWave {
    0%, 100% { opacity: 0.4; background-size: 50px 50px; }
    25% { opacity: 0.7; background-size: 45px 45px; }
    50% { opacity: 0.6; background-size: 55px 55px; }
    75% { opacity: 0.8; background-size: 48px 48px; }
  }
`;

const HeroBackground = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(circle at 15% 85%, rgba(0, 122, 255, 0.08) 0%, transparent 40%),
    radial-gradient(circle at 85% 15%, rgba(255, 59, 48, 0.06) 0%, transparent 40%),
    radial-gradient(circle at 50% 50%, rgba(48, 209, 88, 0.04) 0%, transparent 40%);
`;

const FloatingElements = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
`;

const GlassMorphElement = styled(motion.div)`
  position: absolute;
  background: rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: ${props => props.radius || '30px'};
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.12),
    inset 0 2px 0 rgba(255, 255, 255, 0.7);
`;

const ColoredShape = styled(motion.div)`
  position: absolute;
  background: ${props => props.gradient};
  backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: ${props => props.radius || '50%'};
  box-shadow: 
    0 6px 24px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.lg};
  text-align: center;
  position: relative;
  z-index: 2;
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(3rem, 7vw, 7rem);
  font-weight: 900;
  margin-bottom: ${props => props.theme.spacing.lg};
  background: linear-gradient(135deg, 
    #1a1a1a 0%, 
    #007AFF 20%, 
    #5856D6 40%, 
    #FF3B30 60%, 
    #30D158 80%, 
    #1a1a1a 100%);
  background-size: 500% 500%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: colorShift 10s ease infinite;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  
  @keyframes colorShift {
    0%, 100% { background-position: 0% 50%; }
    25% { background-position: 100% 0%; }
    50% { background-position: 100% 100%; }
    75% { background-position: 0% 100%; }
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.4rem;
  color: rgba(26, 26, 26, 0.8);
  margin-bottom: ${props => props.theme.spacing.xxl};
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
`;

const ScrollIndicator = styled(motion.div)`
  position: absolute;
  bottom: ${props => props.theme.spacing.xxl};
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  color: rgba(26, 26, 26, 0.6);
  cursor: pointer;
  
  svg {
    font-size: 1.5rem;
    animation: pulse 2s ease-in-out infinite;
  }
  
  @keyframes pulse {
    0%, 100% { transform: translateY(0) scale(1); }
    50% { transform: translateY(8px) scale(1.1); }
  }
  
  &:hover {
    color: #007AFF;
  }
`;

// Services Grid with Advanced Layouts
const ServicesSection = styled.section`
  padding: ${props => props.theme.spacing.xxxl} 0;
  background: ${props => props.theme.colors.background};
  position: relative;
  overflow: hidden;
`;

const ServicesContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.lg};
`;

const ServicesHeader = styled.div`
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.xxxl};
`;

const ServicesTitle = styled(motion.h2)`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.text};
`;

const ServicesDescription = styled(motion.p)`
  font-size: 1.2rem;
  color: ${props => props.theme.colors.textLight};
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: ${props => props.theme.spacing.xl};
  margin-bottom: ${props => props.theme.spacing.xxxl};
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: ${props => props.theme.radius.xlarge};
  padding: ${props => props.theme.spacing.xxl};
  position: relative;
  overflow: hidden;
  cursor: pointer;
  height: 400px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${props => props.color1}, ${props => props.color2});
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
  }
  
  &:hover:before {
    transform: scaleX(1);
  }
  
  &:after {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: conic-gradient(from 0deg, transparent, ${props => props.color1}20, transparent);
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }
  
  &:hover:after {
    opacity: 1;
    animation: rotate 3s linear infinite;
  }
  
  @keyframes rotate {
    to { transform: rotate(360deg); }
  }
`;

const ServiceIcon = styled(motion.div)`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, ${props => props.color1}, ${props => props.color2});
  border-radius: ${props => props.theme.radius.medium};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${props => props.theme.spacing.lg};
  position: relative;
  z-index: 2;
  
  svg {
    font-size: 2rem;
    color: white;
  }
  
  &:after {
    content: '';
    position: absolute;
    top: -4px;
    left: -4px;
    right: -4px;
    bottom: -4px;
    background: linear-gradient(135deg, ${props => props.color1}, ${props => props.color2});
    border-radius: ${props => props.theme.radius.medium};
    z-index: -1;
    filter: blur(15px);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
`;

const ServiceTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 600;
  margin-bottom: ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.text};
  position: relative;
  z-index: 2;
`;

const ServiceDescription = styled.p`
  color: ${props => props.theme.colors.textLight};
  line-height: 1.6;
  margin-bottom: ${props => props.theme.spacing.lg};
  position: relative;
  z-index: 2;
`;

const ServiceLink = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
  font-size: 1.1rem;
  position: relative;
  z-index: 2;
  
  svg {
    transition: transform 0.2s ease;
  }
  
  &:hover svg {
    transform: translateX(5px);
  }
`;

// Process Section with Timeline
const ProcessSection = styled.section`
  padding: ${props => props.theme.spacing.xxxl} 0;
  background: linear-gradient(180deg, 
    ${props => props.theme.colors.background} 0%, 
    ${props => props.theme.colors.backgroundAlt} 100%);
  position: relative;
`;

const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${props => props.theme.spacing.xl};
  
  @media (max-width: 968px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const ProcessStep = styled(motion.div)`
  text-align: center;
  position: relative;
`;

const ProcessNumber = styled(motion.div)`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.primaryDark});
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  margin: 0 auto ${props => props.theme.spacing.lg};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    top: -4px;
    left: -4px;
    right: -4px;
    bottom: -4px;
    background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.primaryDark});
    border-radius: 50%;
    z-index: -1;
    filter: blur(20px);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
`;

const ProcessTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: ${props => props.theme.spacing.sm};
  color: ${props => props.theme.colors.text};
`;

const ProcessDescription = styled.p`
  color: ${props => props.theme.colors.textLight};
  font-size: 0.95rem;
  line-height: 1.5;
`;

// CTA Section with Modern Design
const CtaSection = styled.section`
  padding: ${props => props.theme.spacing.xxxl} 0;
  background: ${props => props.theme.colors.backgroundDark};
  color: white;
  position: relative;
  overflow: hidden;
`;

const CtaBackground = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(circle at 20% 80%, rgba(0, 122, 255, 0.2) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255, 59, 48, 0.1) 0%, transparent 50%);
`;

const CtaContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  position: relative;
  z-index: 2;
  padding: 0 ${props => props.theme.spacing.lg};
`;

const CtaTitle = styled(motion.h2)`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: ${props => props.theme.spacing.lg};
  color: white;
`;

const CtaDescription = styled(motion.p)`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: ${props => props.theme.spacing.xxl};
  line-height: 1.6;
`;

const CtaButton = styled(motion.button)`
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.primaryDark});
  color: white;
  padding: ${props => props.theme.spacing.lg} ${props => props.theme.spacing.xxl};
  border: none;
  border-radius: ${props => props.theme.radius.medium};
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  box-shadow: ${props => props.theme.shadows.large};
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }
  
  &:hover:before {
    left: 100%;
  }
  
  svg {
    transition: transform 0.2s ease;
  }
  
  &:hover svg {
    transform: translateX(5px);
  }
`;

// Magnetic Element Component
const MagneticElement = ({ children, strength = 0.3, ...props }) => {
  const ref = useRef(null);
  const isMobile = useIsMobile();
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 300, damping: 30 });
  const springY = useSpring(y, { stiffness: 300, damping: 30 });

  const handleMouseMove = (e) => {
    // Disable magnetic effect on mobile devices
    if (isMobile || !ref.current) return;
    
    const rect = ref.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    x.set((e.clientX - centerX) * strength);
    y.set((e.clientY - centerY) * strength);
  };

  const handleMouseLeave = () => {
    // Disable magnetic effect on mobile devices
    if (isMobile) return;
    
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      ref={ref}
      style={isMobile ? {} : { x: springX, y: springY }}
      onMouseMove={isMobile ? undefined : handleMouseMove}
      onMouseLeave={isMobile ? undefined : handleMouseLeave}
      {...props}
    >
      {children}
    </motion.div>
  );
};

const Services = ({ setCursorVariant }) => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });

  const parallaxY = useTransform(scrollYProgress, [0, 1], [0, -200]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    document.title = "Servizi | D-Soft";
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      id: "sviluppo-web",
      icon: FaCode,
      title: "Sviluppo Web Avanzato",
      description: "Creiamo siti web e applicazioni web moderne con tecnologie all'avanguardia. Dalle landing page ai portali complessi, ogni progetto è ottimizzato per performance e user experience eccezionali.",
      features: ["React & Vue.js", "Node.js & Python", "Database Design", "API Development"],
      color1: "#007AFF",
      color2: "#5856D6",
      link: "/servizio/sviluppo-web"
    },
    {
      id: "app-mobile",
      icon: FaMobile,
      title: "Sviluppo App Mobile",
      description: "Applicazioni native iOS e Android che offrono esperienze fluide e coinvolgenti. Sviluppiamo app che gli utenti adorano utilizzare ogni giorno.",
      features: ["iOS & Android", "React Native", "Flutter", "App Store Optimization"],
      color1: "#30D158",
      color2: "#32D74B",
      link: "/servizio/app-mobile"
    },
    {
      id: "realta-virtuale",
      icon: FaVrCardboard,
      title: "Realtà Virtuale e Aumentata",
      description: "Esperienze immersive che trasportano gli utenti in mondi digitali straordinari. Dalle applicazioni educative al marketing esperienziale.",
      features: ["Unity 3D", "Meta Quest", "ARCore & ARKit", "WebXR"],
      color1: "#FF3B30",
      color2: "#FF9500",
      link: "/servizio/realta-virtuale"
    },
    {
      id: "intelligenza-artificiale",
      icon: FaBrain,
      title: "Intelligenza Artificiale",
      description: "Soluzioni AI personalizzate per automatizzare processi aziendali, analizzare dati e migliorare l'efficienza operativa con algoritmi di machine learning avanzati.",
      features: ["Machine Learning", "Analisi Predittiva", "Automazione Processi", "Chatbot Intelligenti"],
      color1: "#FFD60A",
      color2: "#FFCC02",
      link: "/servizio/intelligenza-artificiale"
    },
    {
      id: "cloud-solutions",
      icon: FaCloud,
      title: "Soluzioni Cloud",
      description: "Infrastrutture cloud scalabili, architetture serverless e soluzioni DevOps per aziende moderne.",
      color1: "#FFD60A",
      color2: "#FFCC02",
      link: "/contatti"
    },
    {
      id: "data-engineering",
      icon: FaDatabase,
      title: "Data Engineering",
      description: "Elaborazione big data, pipeline di analytics e soluzioni machine learning per insights aziendali.",
      color1: "#8E44AD",
      color2: "#9B59B6",
      link: "/contatti"
    },
    {
      id: "cybersecurity",
      icon: FaShieldAlt,
      title: "Cybersecurity",
      description: "Audit di sicurezza completi, penetration testing e architetture di sicurezza per protezione totale.",
      color1: "#E74C3C",
      color2: "#C0392B",
      link: "/contatti"
    }
  ];

  const processSteps = [
    {
      number: "01",
      title: "Scoperta",
      description: "Analizziamo le tue esigenze, obiettivi e sfide per creare la strategia di soluzione perfetta."
    },
    {
      number: "02", 
      title: "Design",
      description: "I nostri esperti progettano interfacce intuitive e architetture robuste su misura per la tua visione."
    },
    {
      number: "03",
      title: "Sviluppo", 
      description: "Costruiamo la tua soluzione utilizzando tecnologie all'avanguardia e best practices."
    },
    {
      number: "04",
      title: "Consegna",
      description: "Lancio, ottimizzazione e supporto continuo per garantire il tuo successo."
    }
  ];

  const scrollToContent = () => {
    window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
  };

  return (
    <div>
      {/* Hero Section */}
      <HeroSection ref={heroRef}>
        <HeroBackground style={{ y: parallaxY, opacity }} />
        <FloatingElements>
          {/* Main Glass Morphism Elements */}
          <GlassMorphElement
            radius="50px"
            animate={{
              x: [0, 120, -80, 0],
              y: [0, -70, 90, 0],
              rotate: [0, 120, 240, 360],
              scale: [1, 1.1, 0.9, 1],
            }}
            transition={{
              duration: 32,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={{
              width: '220px',
              height: '220px',
              top: '12%',
              left: '10%',
            }}
          />
          
          <GlassMorphElement
            radius="50%"
            animate={{
              x: [0, -150, 100, 0],
              y: [0, 100, -80, 0],
              rotate: [0, -90, -180, -270, -360],
            }}
            transition={{
              duration: 38,
              repeat: Infinity,
              ease: "linear",
            }}
            style={{
              width: '180px',
              height: '180px',
              top: '65%',
              right: '8%',
            }}
          />

          {/* Colored Floating Shapes */}
          <ColoredShape
            gradient="linear-gradient(135deg, rgba(0, 122, 255, 0.15) 0%, rgba(88, 86, 214, 0.08) 100%)"
            radius="40px"
            animate={{
              x: [0, 80, -40, 0],
              y: [0, -60, 70, 0],
              rotate: [0, 180, 360],
            }}
            transition={{
              duration: 25,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={{
              width: '140px',
              height: '140px',
              top: '8%',
              right: '20%',
            }}
          />
          
          <ColoredShape
            gradient="linear-gradient(135deg, rgba(255, 59, 48, 0.12) 0%, rgba(255, 149, 0, 0.06) 100%)"
            radius="50%"
            animate={{
              x: [0, -90, 60, 0],
              y: [0, 80, -50, 0],
              scale: [1, 1.2, 0.8, 1],
            }}
            transition={{
              duration: 28,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={{
              width: '120px',
              height: '120px',
              top: '45%',
              left: '15%',
            }}
          />
          
          <ColoredShape
            gradient="linear-gradient(135deg, rgba(48, 209, 88, 0.12) 0%, rgba(50, 215, 75, 0.06) 100%)"
            radius="30px"
            animate={{
              x: [0, 70, -35, 0],
              y: [0, -45, 65, 0],
              rotate: [0, 90, 180, 270, 360],
            }}
            transition={{
              duration: 22,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={{
              width: '100px',
              height: '100px',
              top: '75%',
              right: '25%',
            }}
          />
          
          <ColoredShape
            gradient="linear-gradient(135deg, rgba(255, 204, 0, 0.12) 0%, rgba(255, 214, 10, 0.06) 100%)"
            radius="50%"
            animate={{
              x: [0, 110, -55, 0],
              y: [0, -85, 95, 0],
              rotate: [0, -120, -240, -360],
              scale: [1, 0.9, 1.1, 1],
            }}
            transition={{
              duration: 35,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={{
              width: '160px',
              height: '160px',
              top: '25%',
              left: '70%',
            }}
          />

          {/* Geometric Overlays */}
          <motion.div
            style={{
              position: 'absolute',
              width: '300px',
              height: '300px',
              top: '10%',
              right: '15%',
              background: `
                conic-gradient(from 0deg, 
                  rgba(0, 122, 255, 0.05) 0deg,
                  rgba(255, 59, 48, 0.03) 90deg,
                  rgba(48, 209, 88, 0.04) 180deg,
                  rgba(255, 204, 0, 0.03) 270deg,
                  rgba(0, 122, 255, 0.05) 360deg)
              `,
              borderRadius: '50%',
              opacity: 0.6,
            }}
            animate={{
              rotate: [0, 360],
              scale: [1, 1.08, 1],
            }}
            transition={{
              duration: 45,
              repeat: Infinity,
              ease: "linear",
            }}
          />

          {/* Wave Pattern */}
          <motion.div
            style={{
              position: 'absolute',
              width: '400px',
              height: '80px',
              top: '40%',
              left: '5%',
              background: `
                repeating-linear-gradient(90deg,
                  rgba(0, 122, 255, 0.05) 0px,
                  rgba(0, 122, 255, 0.02) 10px,
                  transparent 20px,
                  rgba(88, 86, 214, 0.03) 30px,
                  transparent 40px)
              `,
              borderRadius: '40px',
              opacity: 0.8,
            }}
            animate={{
              x: [0, 50, -30, 0],
              scaleY: [1, 1.2, 1],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        </FloatingElements>
        
        <HeroContent>
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            <HeroTitle
              onMouseEnter={() => setCursorVariant("text")}
              onMouseLeave={() => setCursorVariant("default")}
            >
              Servizi che
              <br />
              Trasformano
            </HeroTitle>
            
            <HeroSubtitle
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              Dal concept al completamento, forniamo soluzioni all'avanguardia 
              che guidano l'innovazione e accelerano la crescita del tuo business.
            </HeroSubtitle>
          </motion.div>
        </HeroContent>

        <ScrollIndicator
          onClick={scrollToContent}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.8 }}
          onMouseEnter={() => setCursorVariant("button")}
          onMouseLeave={() => setCursorVariant("default")}
        >
          <span>Esplora i Servizi</span>
          <FaChevronDown />
        </ScrollIndicator>
      </HeroSection>

      {/* Services Section */}
      <ServicesSection>
        <ServicesContainer>
          <ServicesHeader>
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <ServicesTitle
                onMouseEnter={() => setCursorVariant("text")}
                onMouseLeave={() => setCursorVariant("default")}
              >
                Cosa Facciamo Meglio
              </ServicesTitle>
              
              <ServicesDescription>
                Soluzioni complete in ogni aspetto della tecnologia moderna,
                fornite da esperti che comprendono le tue esigenze aziendali.
              </ServicesDescription>
            </motion.div>
          </ServicesHeader>

          <ServicesGrid>
            {services.map((service, index) => (
              <MagneticElement key={index} strength={0.1}>
                <Link to={service.link} style={{ textDecoration: 'none', color: 'inherit' }}>
                  <ServiceCard
                    color1={service.color1}
                    color2={service.color2}
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    whileHover={{ 
                      scale: 1.02, 
                      rotateY: 2,
                      transition: { duration: 0.3 }
                    }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.15, duration: 0.8 }}
                    onMouseEnter={() => setCursorVariant("link")}
                    onMouseLeave={() => setCursorVariant("default")}
                  >
                    <div>
                      <ServiceIcon
                        color1={service.color1}
                        color2={service.color2}
                        whileHover={{ 
                          scale: 1.1, 
                          rotate: 5 
                        }}
                      >
                        <service.icon />
                      </ServiceIcon>
                      
                      <ServiceTitle>{service.title}</ServiceTitle>
                      <ServiceDescription>{service.description}</ServiceDescription>
                    </div>
                    
                    <ServiceLink>
                      Scopri di Più
                      <FaArrowRight />
                    </ServiceLink>
                  </ServiceCard>
                </Link>
              </MagneticElement>
            ))}
          </ServicesGrid>
        </ServicesContainer>
      </ServicesSection>

      {/* Process Section */}
      <ProcessSection>
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            style={{ textAlign: 'center', marginBottom: '4rem' }}
          >
            <h2 
              style={{ fontSize: '3rem', marginBottom: '1rem' }}
              onMouseEnter={() => setCursorVariant("text")}
              onMouseLeave={() => setCursorVariant("default")}
            >
              Il Nostro Processo
            </h2>
            <p style={{ fontSize: '1.2rem', color: '#86868B', maxWidth: '600px', margin: '0 auto' }}>
              Una metodologia collaudata che garantisce qualità, efficienza e successo in ogni progetto
            </p>
          </motion.div>

          <ProcessGrid>
            {processSteps.map((step, index) => (
              <MagneticElement key={index} strength={0.1}>
                <ProcessStep
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2, duration: 0.8 }}
                  onMouseEnter={() => setCursorVariant("text")}
                  onMouseLeave={() => setCursorVariant("default")}
                >
                  <ProcessNumber
                    whileHover={{ 
                      scale: 1.1,
                      boxShadow: "0 20px 40px rgba(0, 122, 255, 0.3)"
                    }}
                  >
                    {step.number}
                  </ProcessNumber>
                  <ProcessTitle>{step.title}</ProcessTitle>
                  <ProcessDescription>{step.description}</ProcessDescription>
                </ProcessStep>
              </MagneticElement>
            ))}
          </ProcessGrid>
        </div>
      </ProcessSection>

      {/* CTA Section */}
      <CtaSection>
        <CtaBackground />
        <CtaContent>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <CtaTitle
              onMouseEnter={() => setCursorVariant("text")}
              onMouseLeave={() => setCursorVariant("default")}
            >
              Pronto a Iniziare?
            </CtaTitle>
            
            <CtaDescription>
              Discutiamo il tuo progetto ed esploriamo come possiamo aiutarti a
              raggiungere i tuoi obiettivi con soluzioni tecnologiche innovative.
            </CtaDescription>
            
            <Link to="/contatti">
              <MagneticElement>
                <CtaButton
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onMouseEnter={() => setCursorVariant("button")}
                  onMouseLeave={() => setCursorVariant("default")}
                >
                  Inizia il Tuo Progetto
                  <FaArrowRight />
                </CtaButton>
              </MagneticElement>
            </Link>
          </motion.div>
        </CtaContent>
      </CtaSection>
    </div>
  );
};

export default Services; 